import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatUSD, formatDate } from "@/lib/utils";
import {
  Loader2,
  Activity,
  CheckCircle2,
  XCircle,
  Circle,
  Save,
  RotateCcw,
  AlertTriangle,
} from "lucide-react";

const MODE_STYLES = {
  live: "bg-emerald-50 text-emerald-700 border-emerald-200",
  mock: "bg-amber-50 text-amber-700 border-amber-200",
  disabled: "bg-slate-100 text-slate-500 border-slate-200",
};

const HEALTH_ICON = {
  healthy: { icon: CheckCircle2, cls: "text-emerald-600" },
  unhealthy: { icon: XCircle, cls: "text-red-600" },
  unknown: { icon: Circle, cls: "text-slate-400" },
};

function ModeBadge({ mode }) {
  const cls = MODE_STYLES[mode] || MODE_STYLES.disabled;
  return (
    <span
      className={
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest border " +
        cls
      }
      data-testid={`provider-mode-${mode}`}
    >
      <span
        className={
          "w-1.5 h-1.5 rounded-full " +
          (mode === "live"
            ? "bg-emerald-500"
            : mode === "mock"
            ? "bg-amber-500"
            : "bg-slate-400")
        }
      />
      {mode}
    </span>
  );
}

export default function ProviderOrchestratorPanel() {
  const [items, setItems] = useState([]);
  const [chain, setChain] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(null);
  const [saving, setSaving] = useState(false);

  // Draft state — mirrors items but stores the user's staged edits until
  // "Save Priority" commits them via the batch endpoint.
  const [draft, setDraft] = useState({}); // { [key]: { priority, enabled } }

  const load = async () => {
    try {
      const { data } = await api.get("/admin/providers");
      setItems(data.items || []);
      setChain(data.priority_chain || []);
      // Reset draft to current server state.
      const d = {};
      (data.items || []).forEach((r) => {
        d[r.key] = { priority: r.priority, enabled: !!r.enabled };
      });
      setDraft(d);
    } catch {
      /* master-only */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setDraftField = (key, field, value) => {
    setDraft((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  // Ordered draft rows (by draft priority) — this is what the user sees.
  const orderedRows = useMemo(() => {
    return items
      .map((r) => ({ ...r, ...draft[r.key] }))
      .sort((a, b) => (a.priority || 0) - (b.priority || 0));
  }, [items, draft]);

  const duplicatePriorities = useMemo(() => {
    const seen = new Set();
    const dupes = new Set();
    Object.values(draft).forEach((d) => {
      if (seen.has(d.priority)) dupes.add(d.priority);
      seen.add(d.priority);
    });
    return dupes;
  }, [draft]);

  const isDirty = useMemo(() => {
    return items.some(
      (r) =>
        draft[r.key] &&
        (draft[r.key].priority !== r.priority ||
          !!draft[r.key].enabled !== !!r.enabled)
    );
  }, [items, draft]);

  const priorityOptions = useMemo(
    () => items.map((_, i) => i + 1),
    [items]
  );

  const runHealth = async (key) => {
    setChecking(key);
    const t = toast.loading("Running health check…");
    try {
      const { data } = await api.post(`/admin/providers/${key}/healthcheck`);
      toast.success(
        data.healthy
          ? `Healthy · ${data.latency_ms || "?"}ms`
          : `Unhealthy: ${data.detail || "unknown"}`,
        { id: t }
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Health check failed", { id: t });
    } finally {
      setChecking(null);
    }
  };

  const save = async () => {
    if (duplicatePriorities.size > 0) {
      toast.error("Two providers cannot share the same priority. Fix duplicates first.");
      return;
    }
    setSaving(true);
    const t = toast.loading("Saving priority order…");
    try {
      const payload = {
        items: Object.entries(draft).map(([key, d]) => ({
          key,
          priority: d.priority,
          enabled: d.enabled,
        })),
      };
      const { data } = await api.put("/admin/providers/priorities", payload);
      const changedKeys = Object.keys(data.changes || {});
      toast.success(
        changedKeys.length
          ? `Saved · ${changedKeys.length} provider${changedKeys.length > 1 ? "s" : ""} updated`
          : "Saved · no changes",
        { id: t }
      );
      setItems(data.items || []);
      setChain(data.priority_chain || []);
      const d = {};
      (data.items || []).forEach((r) => {
        d[r.key] = { priority: r.priority, enabled: !!r.enabled };
      });
      setDraft(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save priorities", { id: t });
    } finally {
      setSaving(false);
    }
  };

  const resetDraft = () => {
    const d = {};
    items.forEach((r) => {
      d[r.key] = { priority: r.priority, enabled: !!r.enabled };
    });
    setDraft(d);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 flex items-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading providers…
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4"
      data-testid="provider-orchestrator-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-900">
            Provider Orchestrator
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Set the sending priority manually — the orchestrator tries #1
            first, then #2, then #3. Disabled providers are skipped entirely.
            Fallback happens only on connection errors, never on rejected
            transactions.
          </div>
          {chain.length > 0 && (
            <div
              className="text-xs text-slate-600 mt-2"
              data-testid="provider-chain-preview"
            >
              <span className="font-semibold">Active chain: </span>
              {chain.map((k, i) => (
                <React.Fragment key={k}>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold">
                    {i + 1}. {k}
                  </span>
                  {i < chain.length - 1 && (
                    <span className="mx-1 text-slate-400">→</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <button
              onClick={resetDraft}
              disabled={saving}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
              data-testid="reset-priority-btn"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset
            </button>
          )}
          <button
            onClick={save}
            disabled={saving || !isDirty || duplicatePriorities.size > 0}
            className={
              "inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-sm font-semibold transition-colors " +
              (isDirty && duplicatePriorities.size === 0
                ? "bg-[#0F62FE] text-white hover:bg-[#0043CE]"
                : "bg-slate-100 text-slate-400 cursor-not-allowed")
            }
            data-testid="save-priority-btn"
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            Save Priority
          </button>
        </div>
      </div>

      {duplicatePriorities.size > 0 && (
        <div
          className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 text-xs"
          data-testid="duplicate-priority-warning"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600" />
          <span>
            Two providers currently share the same priority (
            {[...duplicatePriorities].join(", ")}). Pick a different value
            before saving.
          </span>
        </div>
      )}

      <div className="divide-y divide-slate-200 rounded-lg border border-slate-200">
        {orderedRows.map((row) => {
          const healthState = !row.last_health
            ? "unknown"
            : row.last_health.healthy
            ? "healthy"
            : "unhealthy";
          const HIcon = HEALTH_ICON[healthState].icon;
          const draftRow = draft[row.key] || {};
          const isDup = duplicatePriorities.has(draftRow.priority);
          const rowChanged =
            draftRow.priority !== row.priority ||
            !!draftRow.enabled !== !!row.enabled;
          return (
            <div
              key={row.key}
              className={
                "p-4 flex items-start gap-4 flex-wrap " +
                (rowChanged ? "bg-blue-50/40" : "")
              }
              data-testid={`provider-row-${row.key}`}
            >
              <div className="flex flex-col items-center gap-1 pt-1 min-w-[80px]">
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  Priority
                </label>
                <select
                  value={draftRow.priority ?? row.priority ?? 1}
                  onChange={(e) =>
                    setDraftField(row.key, "priority", parseInt(e.target.value, 10))
                  }
                  disabled={saving}
                  data-testid={`priority-select-${row.key}`}
                  className={
                    "h-9 px-2 rounded-lg border text-sm font-bold text-center bg-white " +
                    (isDup
                      ? "border-amber-500 text-amber-700"
                      : "border-slate-300 text-slate-900")
                  }
                >
                  {priorityOptions.map((v) => (
                    <option key={v} value={v}>
                      #{v}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="text-sm font-bold text-slate-900"
                    style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  >
                    {row.display_name || row.key}
                  </span>
                  <ModeBadge mode={row.mode || "disabled"} />
                  {row.configured ? null : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 uppercase tracking-widest font-semibold">
                      Keys pending
                    </span>
                  )}
                  {rowChanged && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 uppercase tracking-widest font-semibold">
                      Unsaved
                    </span>
                  )}
                </div>
                {row.notes && (
                  <div className="text-xs text-slate-500 mt-1">{row.notes}</div>
                )}
                {row.last_health && (
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-1 flex-wrap">
                    <HIcon className={"w-3.5 h-3.5 " + HEALTH_ICON[healthState].cls} />
                    <span>{row.last_health.detail || "—"}</span>
                    {row.last_health.balance != null && (
                      <span>
                        · Balance {formatUSD(row.last_health.balance)}{" "}
                        {row.last_health.currency || ""}
                      </span>
                    )}
                    <span>· {formatDate(row.last_health.checked_at)}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <label
                  className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer select-none"
                  data-testid={`provider-enabled-label-${row.key}`}
                >
                  <input
                    type="checkbox"
                    checked={!!draftRow.enabled}
                    disabled={saving}
                    onChange={(e) =>
                      setDraftField(row.key, "enabled", e.target.checked)
                    }
                    className="rounded border-slate-300"
                    data-testid={`provider-enabled-${row.key}`}
                  />
                  Enabled
                </label>
                <button
                  onClick={() => runHealth(row.key)}
                  disabled={checking === row.key}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
                  data-testid={`provider-healthcheck-${row.key}`}
                >
                  {checking === row.key ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Activity className="w-3.5 h-3.5" />
                  )}
                  Health check
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
